/* eslint-disable no-nested-ternary */
/** @jsxImportSource @emotion/react */
import { useState } from 'react';
import { bool, func, array, string } from 'prop-types';
import {
  Box,
  ModalStyled,
  ModalActionsStyled,
  TextStyled,
  ButtonStyled,
  useTokens
} from '@nike/nike-design-system-components';

import Spinner from '../../../components/spinner/Spinner';
import StatusModal from '../../common/StatusModal';

import LineItem from './LineItem';
import EditLineItemModal from './EditLineItemModal';
import ConfirmDeleteModal from './ConfirmDeleteModal';
import ConfirmResubmitModal from './ConfirmResubmitModal';

import useModal from '../../../hooks/useModal';

import { updateLineItemSizes, deleteLineItem, resubmitOrder } from '../actions';

const LineItemsModal = ({ isOpen, closeModal, orderId, lineItems, lineItemsLoading, refreshLineItems }) => {
  const tokens = useTokens();

  const [isEditLineItemModalOpen, openEditLineItemModal, closeEditLineItemModal] = useModal();
  const [itemToEdit, setItemToEdit] = useState(null);

  const [isConfirmDeleteModalOpen, openConfirmDeleteModal, closeConfirmDeleteModal] = useModal();
  const [itemToDelete, setItemToDelete] = useState(null);

  const [isConfirmResubmitModalOpen, openConfirmResubmitModal, closeConfirmResubmitModal] = useModal();

  const [requestInProgress, setRequestInProgress] = useState(false);

  const [isStatusModalOpen, openStatusModal, closeStatusModal] = useModal();
  const [statusModalProps, setStatusModalProps] = useState({ title: '' });

  const handleClickEdit = item => {
    setItemToEdit(item);
    openEditLineItemModal();
  };

  const handleConfirmEdit = async updatedSizeQuantities => {
    const adaptedSizeQuantities = updatedSizeQuantities.map(sq => ({
      sizeCode: sq.sizeCode,
      quantity: sq.quantity
    }));
    setRequestInProgress(true);
    try {
      await updateLineItemSizes({
        orderId,
        lineItemId: itemToEdit.id,
        sizes: adaptedSizeQuantities
      });
      closeEditLineItemModal();
      await refreshLineItems(orderId);
      setItemToEdit(null);
    } catch (err) {
      setStatusModalProps({
        type: 'error',
        title: 'Error',
        message: `Error updating line item sizes: ${err.errorMessage || 'Unknown error'}`
      });
      openStatusModal();
    }
    setRequestInProgress(false);
  };

  const handleClickDelete = item => {
    setItemToDelete(item);
    openConfirmDeleteModal();
  };

  const handleConfirmDelete = async () => {
    setRequestInProgress(true);
    try {
      await deleteLineItem({ orderId, lineItemId: itemToDelete.id });
      closeConfirmDeleteModal();
      await refreshLineItems(orderId);
      setItemToDelete(null);
    } catch (err) {
      setStatusModalProps({
        type: 'error',
        title: 'Error',
        message: `Error deleting line item: ${err.errorMessage || 'Unknown error'}`
      });
      openStatusModal();
    }
    setRequestInProgress(false);
  };

  const handleConfirmResubmit = async orderId => {
    setRequestInProgress(true);
    try {
      await resubmitOrder(orderId);
      closeConfirmResubmitModal();
      setStatusModalProps({
        type: 'success',
        title: 'Success',
        message: 'Order resubmitted successfully.'
      });
      openStatusModal();
    } catch (err) {
      console.log(`submit error? ${JSON.stringify(err)}`);
      closeConfirmResubmitModal();
      setStatusModalProps({
        type: 'error',
        title: 'Error',
        message: `Error resubmitting order: ${err.errorMessage || 'Unknown error'}`
      });
      openStatusModal();
    }
    setRequestInProgress(false);
  };

  return (
    <ModalStyled
      isOpen={isOpen}
      closeModal={closeModal}
      title=''
      modalRootCompProps={{ style: { maxWidth: '1200px' } }}
      backdropCompProps={{ style: { backdropFilter: 'blur(4px)' } }}
      slots={{
        HeaderTitleSlot: (
          <div style={{ borderBottom: `1px solid ${tokens.podiumCdsColorGrey300}` }}>
            <TextStyled appearance='title2'>Order Line Items</TextStyled>
            <TextStyled appearance='body1Strong' color={tokens.podiumCdsColorGrey500}>
              {lineItems.length} Items
            </TextStyled>
          </div>
        ),
        FooterSlot: (
          <ModalActionsStyled>
            <>
              <ButtonStyled
                type='submit'
                appearance='primary'
                onClick={openConfirmResubmitModal}
                disabled={lineItemsLoading || !lineItems.length}
              >
                Resubmit Order
              </ButtonStyled>
              <ButtonStyled type='button' appearance='secondary' onClick={closeModal}>
                Cancel
              </ButtonStyled>
            </>
          </ModalActionsStyled>
        )
      }}
    >
      <Box width='100%' height='100%'>
        <Box paddingBlockEnd='10px'>
          {lineItemsLoading ? (
            <Spinner className='relative-container' />
          ) : lineItems.length ? (
            <Box
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '16px'
              }}
            >
              {lineItems.map((item, index) => (
                <LineItem
                  key={item.id}
                  item={item}
                  index={index}
                  handleDeleteItem={handleClickDelete}
                  handleEditItem={handleClickEdit}
                />
              ))}
            </Box>
          ) : (
            <TextStyled appearance='body2'>No line items found</TextStyled>
          )}
        </Box>

        {isEditLineItemModalOpen && (
          <EditLineItemModal
            isOpen={isEditLineItemModalOpen}
            closeModal={closeEditLineItemModal}
            onConfirm={handleConfirmEdit}
            item={itemToEdit}
            requestInProgress={requestInProgress}
          />
        )}

        {isConfirmDeleteModalOpen && (
          <ConfirmDeleteModal
            isOpen={isConfirmDeleteModalOpen}
            closeModal={closeConfirmDeleteModal}
            onConfirm={handleConfirmDelete}
            item={itemToDelete}
            requestInProgress={requestInProgress}
          />
        )}

        {isConfirmResubmitModalOpen && (
          <ConfirmResubmitModal
            isOpen={isConfirmResubmitModalOpen}
            closeModal={closeConfirmResubmitModal}
            onConfirm={handleConfirmResubmit}
            orderId={orderId}
            requestInProgress={requestInProgress}
          />
        )}

        {isStatusModalOpen && (
          <StatusModal isOpen={isStatusModalOpen} closeModal={closeStatusModal} modalProps={statusModalProps} />
        )}
      </Box>
    </ModalStyled>
  );
};

LineItemsModal.propTypes = {
  isOpen: bool.isRequired,
  closeModal: func.isRequired,
  orderId: string.isRequired,
  lineItems: array,
  lineItemsLoading: bool,
  refreshLineItems: func.isRequired
};

LineItemsModal.defaultProps = {
  lineItems: [],
  lineItemsLoading: false
};

export default LineItemsModal;
